import type { AuthSession } from "./types";
import { authenticatedFetch } from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

export type TwilioConnection = {
  connectionId: string;
  organizationId: string;
  label: string;
  accountSidMasked: string;
  senderPhone: string;
  senderAddress: string;
  phoneNumberId: string;
  displayPhoneNumber: string;
  messagingServiceSid: string;
  webhookUrl: string;
  statusCallbackUrl: string;
  status: "pending" | "active" | "paused" | "error";
  credentialsConfigured: boolean;
  createdAt: string;
  updatedAt: string;
  lastCheckedAt?: string;
  lastInboundAt?: string;
  lastError?: string;
};

export type CreateTwilioConnectionInput = {
  label: string;
  accountSid: string;
  authToken: string;
  senderPhone: string;
  displayPhoneNumber?: string;
  messagingServiceSid?: string;
};

export async function listTwilioConnections(session: AuthSession) {
  const body = await request<{ items: TwilioConnection[] }>("/twilio/connections", session);
  return body.items;
}

export async function createTwilioConnection(
  input: CreateTwilioConnectionInput,
  session: AuthSession,
) {
  const body = await request<{ item: TwilioConnection }>("/twilio/connections", session, {
    method: "POST",
    body: JSON.stringify(input),
  });
  return body.item;
}

export async function updateTwilioConnection(
  connectionId: string,
  input: Partial<Pick<TwilioConnection, "label" | "displayPhoneNumber" | "messagingServiceSid" | "status">>,
  session: AuthSession,
) {
  const body = await request<{ item: TwilioConnection }>(
    `/twilio/connections/${encodeURIComponent(connectionId)}`,
    session,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return body.item;
}

export async function updateTwilioCredentials(
  connectionId: string,
  authToken: string,
  session: AuthSession,
) {
  const body = await request<{ item: TwilioConnection }>(
    `/twilio/connections/${encodeURIComponent(connectionId)}/credentials`,
    session,
    { method: "POST", body: JSON.stringify({ authToken }) },
  );
  return body.item;
}

export async function activateTwilioConnection(
  connectionId: string,
  session: AuthSession,
) {
  const body = await request<{ item: TwilioConnection }>(
    `/twilio/connections/${encodeURIComponent(connectionId)}/activate`,
    session,
    { method: "POST" },
  );
  return body.item;
}

async function request<T>(path: string, session: AuthSession, init: RequestInit = {}) {
  if (!API_BASE_URL) throw new Error("API_NOT_CONFIGURED");
  const response = await authenticatedFetch(`${API_BASE_URL}${path}`, session, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof body?.error === "string" ? body.error : `HTTP_${response.status}`;
    throw new Error(code);
  }
  return body as T;
}
